/**
 * AI Chat Service (mocked)
 *
 * Handles chat interactions with the AI assistant.
 * When the Claude API is integrated, this will send messages
 * to the API with the active persona's systemPrompt.
 */

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

const MOCK_RESPONSES: Record<string, string[]> = {
  financeiro: [
    'Com base nos dados do DRE atual, a Monumenta está com a receita bruta em ritmo abaixo do PROA anual. A sazonalidade histórica sugere que os meses de Maio a Julho costumam concentrar maior volume de faturamento, o que pode ajudar na recuperação.',
    'Analisando a estrutura de custos, a folha representa a maior parcela dos gastos e está proporcionalmente crescendo em relação à receita. Isso pressiona a margem líquida. Sugiro revisar a alocação de equipe em projetos de baixa rentabilidade.',
    'Os custos de terceiros contratados mas ainda não lançados somam um valor relevante. É importante considerar esses compromissos na projeção de resultado para os próximos meses.',
    'A receita financeira está contribuindo positivamente para o resultado. Em cenário de juros altos, manter capital aplicado continua sendo uma estratégia válida enquanto o fluxo de caixa permitir.',
  ],
  performance: [
    'O ciclo de avaliações atual mostra uma distribuição saudável de notas, com a maioria dos colaboradores na faixa "atende" e "supera". Recomendo atenção aos colaboradores na faixa inferior para planos de desenvolvimento.',
    'Os feedbacks recentes indicam forte reconhecimento das competências técnicas da equipe, mas há oportunidade de desenvolvimento em habilidades de comunicação e gestão de conflitos.',
    'Analisando os OKRs do trimestre, 65% dos objetivos estão no ritmo esperado. Os que estão atrasados concentram-se em projetos que dependem de terceiros — sugiro uma revisão de dependências.',
    'O radar de desempenho mostra que as áreas de Criação e Atendimento têm os melhores indicadores de engajamento, enquanto Tecnologia apresenta sinais de sobrecarga.',
  ],
}

/**
 * Generate a mock response based on the persona module.
 * In production, this will call the Claude API.
 */
export function generateChatResponse(
  _message: string,
  personaId: string,
): Promise<string> {
  return new Promise((resolve) => {
    // Simulate API delay
    const delay = 800 + Math.random() * 1200
    setTimeout(() => {
      const responses = MOCK_RESPONSES[personaId] ?? MOCK_RESPONSES.financeiro!
      const response = responses[Math.floor(Math.random() * responses.length)]!
      resolve(response)
    }, delay)
  })
}

/**
 * Generate a contextual summary for the current screen.
 * In production, this will use screen data + persona prompt.
 */
export function generateContextSummary(personaId: string): string {
  if (personaId === 'performance') {
    return 'O ciclo de avaliações de desempenho está em andamento. A média geral de notas está em 3.8/5, com 78% dos colaboradores já avaliados. Há 5 feedbacks pendentes de revisão.'
  }
  return 'A Monumenta está com receita acumulada abaixo do ritmo PROA. Os custos estão controlados, mas a margem líquida está pressionada pela relação folha/receita. Atenção à concentração de receita em poucos clientes.'
}
